import { useState, useEffect, useRef } from 'react';
import {
    IconArrowLeft,
    IconArrowRight,
    IconBookmark,
    IconClock,
    IconSchool,
    IconCurrencyTaka,
    IconCreditCard,
    IconUsers,
    IconStar,
    IconBuildingCommunity,
    IconWorld,
    IconDownload,
    IconLoader2,
    IconAlertTriangle,
    IconCircleCheck,
} from '@tabler/icons-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from '../../../i18n/hooks/useTranslation';
import jobService from '../../../services/jobService';
import { useSavedJobsStore } from '../../../store/savedJobsStore';
import { useAuthStore } from '../../../store/authStore';
import { useOfficeSpaceStore } from '../../../store/officeSpaceStore';
import { useBiggaponStyleStore } from '../../../store/biggaponStyleStore';
import BiggaponTemplate from '../../../components/biggapon/templates';
import { downloadPoster } from '../../../utils/downloadPoster';
import {
    toBnDigits,
    formatVacancies,
    formatBnDate,
    deadlineLabel,
    deadlineTone,
    daysUntil,
    jobLogoSrc,
} from '../../../utils/bnFormat';

/* ------------------------------------------------------------------ */

const CATEGORY_TAG = {
    government: 'text-[#C5611F] bg-[#FBEEE4] dark:bg-[#C5611F]/15',
    defense: 'text-[#2F7D5B] bg-[#E7F2EC] dark:bg-[#2F7D5B]/15',
    bank: 'text-[#3A6FA0] bg-[#EAF0F6] dark:bg-[#3A6FA0]/15',
    education: 'text-[#B5862B] bg-[#F7F0E0] dark:bg-[#B5862B]/15',
    private: 'text-[#9A5B86] bg-[#F4E9F0] dark:bg-[#9A5B86]/15',
};

const DEADLINE_PILL = {
    red: 'text-[#C0563F] bg-[#F8ECE8] dark:bg-[#C0563F]/15',
    gold: 'text-[#B5862B] bg-[#FAF2E2] dark:bg-[#B5862B]/15',
    green: 'text-[#2F7D5B] bg-[#E7F2EC] dark:bg-[#2F7D5B]/15',
};

// Eligibility key → icon + label key.
const ELIG_META = {
    edu: { labelKey: 'eduQualification', Icon: IconSchool },
    age: { labelKey: 'ageLimit', Icon: IconClock },
    salary: { labelKey: 'salaryScale', Icon: IconCurrencyTaka },
    fee: { labelKey: 'applicationFee', Icon: IconCreditCard },
};

// Resolve an eligibility/qualification item into a display icon + label.
// Preset keys use a translated label; a `custom` key falls back to the
// admin-typed `label`, with a generic icon.
const resolveElig = (item, t) => {
    const preset = ELIG_META[item.key];
    if (preset) return { Icon: preset.Icon, label: t(`jobs.details.elig.${preset.labelKey}`) };
    return { Icon: IconCircleCheck, label: item.label || '' };
};

/* ------------------------------------------------------------------ */

function SectionHeading({ children }) {
    return (
        <div className="mb-4 flex items-center gap-2">
            <span className="h-[18px] w-[3px] rounded-sm bg-primary" />
            <span className="text-[17px] font-bold text-[#1C1B19] dark:text-zinc-100">{children}</span>
        </div>
    );
}

/* ------------------------------------------------------------------ */

export default function JobDetails() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { id } = useParams();

    const [job, setJob] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const savedSet = useSavedJobsStore((s) => s.saved);
    const toggleSaved = useSavedJobsStore((s) => s.toggle);
    const isSaved = job ? savedSet.has(String(job.id)) : false;

    const user = useAuthStore((s) => s.user);
    const selectedOfficeSpace = useOfficeSpaceStore((s) => s.selectedOfficeSpace);
    // Poster footer = the ACTIVE shop (name + address from the selected office's
    // profile; phone stays the owner's). Falls back to user when no office selected.
    const posterBusiness = selectedOfficeSpace
        ? {
              ...user,
              shopName: selectedOfficeSpace.businessProfile?.shopName || selectedOfficeSpace.name,
              businessAddress: selectedOfficeSpace.businessProfile?.businessAddress || '',
          }
        : user;
    const styleId = useBiggaponStyleStore((s) => s.styleId);
    const posterRef = useRef(null);
    const [downloadingPoster, setDownloadingPoster] = useState(false);

    const handleDownloadPoster = async () => {
        setDownloadingPoster(true);
        try {
            await downloadPoster(posterRef.current, `biggapon-${job?.slug || job?.id || 'poster'}.png`);
        } finally {
            setDownloadingPoster(false);
        }
    };

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(false);
        jobService
            .getJob(id)
            .then((res) => {
                if (!cancelled) setJob(res.data);
            })
            .catch(() => {
                if (!cancelled) setError(true);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [id]);

    const BackLink = (
        <button
            type="button"
            onClick={() => navigate('/jobs')}
            className="mb-4 inline-flex items-center gap-[7px] text-[13px] font-semibold text-[#6E6A63] hover:text-[#1C1B19] dark:text-zinc-400 dark:hover:text-zinc-100"
        >
            <IconArrowLeft size={16} stroke={1.9} />
            {t('jobs.details.backToJobs')}
        </button>
    );

    if (loading) {
        return (
            <div className="w-full font-hind py-8 text-[#1C1B19] dark:text-zinc-100">
                {BackLink}
                <div className="h-28 animate-pulse rounded-2xl border border-[#ECE9E3] bg-white dark:border-zinc-700 dark:bg-zinc-900" />
                <div className="mt-5 flex flex-wrap gap-5">
                    <div className="h-80 min-w-[380px] flex-1 animate-pulse rounded-[14px] border border-[#ECE9E3] bg-white dark:border-zinc-700 dark:bg-zinc-900" />
                    <div className="h-80 w-full animate-pulse rounded-[14px] border border-[#ECE9E3] bg-white lg:w-[320px] dark:border-zinc-700 dark:bg-zinc-900" />
                </div>
            </div>
        );
    }

    if (error || !job) {
        return (
            <div className="w-full font-hind py-8 text-[#1C1B19] dark:text-zinc-100">
                {BackLink}
                <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-[#ECE9E3] bg-white py-20 text-center dark:border-zinc-700 dark:bg-zinc-900">
                    <IconAlertTriangle size={36} stroke={1.5} className="text-[#C0563F]" />
                    <p className="mt-3 text-sm font-medium text-[#8A857B] dark:text-zinc-400">{t('jobs.error')}</p>
                    <button
                        type="button"
                        onClick={() => navigate('/jobs')}
                        className="mt-4 rounded-[9px] bg-primary px-4 py-2 text-[13px] font-semibold text-white hover:bg-primary/90"
                    >
                        {t('jobs.details.backToJobs')}
                    </button>
                </div>
            </div>
        );
    }

    const tone = deadlineTone(job.deadlineDate);
    const expired = daysUntil(job.deadlineDate) < 0;
    const vacancies = job.vacancies || [];
    const eligibility = job.eligibility || [];
    const process = job.process || [];

    const glance = [
        { labelKey: 'totalVacancies', value: formatVacancies(job.totalVacancies), Icon: IconUsers },
        { labelKey: 'grade', value: job.gradeLabel || '—', Icon: IconStar },
        { labelKey: 'workplace', value: job.workplace || '—', Icon: IconBuildingCommunity },
        { labelKey: 'applyMethod', value: job.applyMethodLabel || '—', Icon: IconWorld },
    ];

    const timeline = [
        { titleKey: 'applicationStart', date: formatBnDate(job.publishedDate), dotClass: 'bg-[#2F7D5B] shadow-[0_0_0_3px_rgba(47,125,91,0.13)]' },
        { titleKey: 'applicationEnd', date: `${formatBnDate(job.deadlineDate, { withYear: true })} · ১১:৫৯ PM`, dotClass: 'bg-primary shadow-[0_0_0_3px_rgba(230,138,0,0.13)]' },
        { titleKey: 'feePaymentEnd', date: job.feePaymentNote || '—', dotClass: 'bg-[#C9C5BC] shadow-[0_0_0_3px_rgba(201,197,188,0.13)]' },
        { titleKey: 'examDate', date: job.examDateNote || '—', dotClass: 'bg-[#C9C5BC] shadow-[0_0_0_3px_rgba(201,197,188,0.13)]' },
    ];

    return (
        <div className="w-full font-hind py-8 text-[#1C1B19] dark:text-zinc-100">
            {BackLink}

            {/* Hero card */}
            <div className="rounded-2xl border border-[#ECE9E3] bg-white p-6 shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:border-zinc-700 dark:bg-zinc-900">
                <div className="flex flex-wrap items-start gap-[18px]">
                    <div className="flex h-[88px] w-[88px] flex-none items-center justify-center overflow-hidden">
                        <img src={jobLogoSrc(job)} alt="" className="h-full w-full object-contain" />
                    </div>
                    <div className="min-w-[220px] flex-1">
                        <div className="mb-[9px] flex flex-wrap items-center gap-[9px]">
                            <span className={`rounded-[7px] px-[11px] py-1 text-[13px] font-bold ${CATEGORY_TAG[job.category]}`}>
                                {t(`jobs.categories.${job.category}`)}
                            </span>
                            <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-[5px] text-[14px] font-bold ${DEADLINE_PILL[tone]}`}>
                                <IconClock size={14} stroke={2} />
                                {deadlineLabel(job.deadlineDate)}
                            </span>
                        </div>
                        <h1 className="text-[27px] font-bold leading-[1.25] tracking-[-0.01em] text-[#1C1B19] dark:text-zinc-100">
                            {job.organization}
                        </h1>
                        <div className="mt-[5px] text-[17px] text-[#6E6A63] dark:text-zinc-400">{job.position}</div>
                    </div>
                    <div className="flex flex-none items-center gap-[9px]">
                        <button
                            type="button"
                            aria-pressed={isSaved}
                            onClick={() => toggleSaved(job.id)}
                            className={`flex items-center gap-[7px] rounded-[10px] border px-4 py-[11px] text-[14.5px] font-semibold transition-colors ${
                                isSaved
                                    ? 'border-primary bg-primary-50 text-primary dark:bg-primary-900/20'
                                    : 'border-[#E4E1DA] bg-white text-[#56524A] hover:bg-[#FAF8F4] dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800'
                            }`}
                        >
                            <IconBookmark
                                size={16}
                                stroke={1.8}
                                className={isSaved ? 'fill-primary text-primary' : 'text-[#A8A294]'}
                            />
                            {isSaved ? t('jobs.details.saved') : t('jobs.details.save')}
                        </button>
                    </div>
                </div>
            </div>

            {/* Two-pane */}
            <div className="mt-5 flex flex-wrap items-start gap-5">
                {/* Left column */}
                <div className="flex min-w-[380px] flex-1 flex-col gap-[18px]">
                    {/* Vacancies by post */}
                    {vacancies.length > 0 && (
                        <div className="rounded-[14px] border border-[#ECE9E3] bg-white px-[22px] py-5 dark:border-zinc-700 dark:bg-zinc-900">
                            <SectionHeading>{t('jobs.details.vacanciesByPost')}</SectionHeading>
                            <div className="flex items-center border-b border-[#F1EFEA] px-1 pb-[9px] dark:border-zinc-800">
                                <span className="flex-1 text-[13px] font-semibold uppercase tracking-[0.04em] text-[#A8A294]">
                                    {t('jobs.details.th.postName')}
                                </span>
                                <span className="w-[100px] text-center text-[13px] font-semibold uppercase tracking-[0.04em] text-[#A8A294]">
                                    {t('jobs.details.th.grade')}
                                </span>
                                <span className="w-20 text-right text-[13px] font-semibold uppercase tracking-[0.04em] text-[#A8A294]">
                                    {t('jobs.details.th.count')}
                                </span>
                            </div>
                            {vacancies.map((v, i) => {
                                const quals = Array.isArray(v.qualifications) ? v.qualifications.filter((q) => q.value) : [];
                                return (
                                    <div key={i} className="border-b border-[#F4F2EE] px-1 py-[13px] dark:border-zinc-800">
                                        <div className="flex items-center">
                                            <span className="flex-1 text-[16px] font-semibold text-[#1C1B19] dark:text-zinc-100">{v.post}</span>
                                            <span className="w-[100px] text-center">
                                                <span className="rounded-md bg-[#F4F2EE] px-2.5 py-[3px] text-[13.5px] font-semibold text-[#6E6A63] dark:bg-zinc-800 dark:text-zinc-400">
                                                    {v.grade || job.gradeLabel || '—'}
                                                </span>
                                            </span>
                                            <span className="w-20 text-right text-[18px] font-bold text-[#1C1B19] dark:text-zinc-100">{toBnDigits(v.count)}</span>
                                        </div>
                                        {quals.length > 0 && (
                                            <div className="mt-2.5 flex flex-col items-start gap-2">
                                                {quals.map((q, qi) => {
                                                    const { Icon } = resolveElig(q, t);
                                                    return (
                                                        <span key={qi} className="inline-flex items-center gap-1.5 rounded-[9px] border border-[#F1EFEA] bg-[#FBFAF8] px-2.5 py-1.5 text-[14px] dark:border-zinc-800 dark:bg-zinc-800/40">
                                                            <Icon size={15} stroke={1.7} className="flex-none text-primary" />
                                                            <span className="font-sans font-normal text-[#1C1B19] dark:text-zinc-100">{q.value}</span>
                                                        </span>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Eligibility */}
                    {eligibility.length > 0 && (
                        <div className="rounded-[14px] border border-[#ECE9E3] bg-white px-[22px] py-5 dark:border-zinc-700 dark:bg-zinc-900">
                            <SectionHeading>{t('jobs.details.eligibility')}</SectionHeading>
                            <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
                                {eligibility.map((item, i) => {
                                    const { Icon, label } = resolveElig(item, t);
                                    return (
                                        <div key={i} className="flex items-start gap-[11px] rounded-[11px] border border-[#F1EFEA] bg-[#FBFAF8] px-3.5 py-[13px] dark:border-zinc-800 dark:bg-zinc-800/40">
                                            <div className="flex h-[38px] w-[38px] flex-none items-center justify-center rounded-[9px] bg-primary-50 dark:bg-primary-900/20">
                                                <Icon size={19} stroke={1.7} className="text-primary" />
                                            </div>
                                            <div>
                                                <div className="mb-[3px] text-[13px] text-[#8A857B] dark:text-zinc-400">
                                                    {label}
                                                </div>
                                                <div className="font-sans text-[15px] font-normal leading-[1.4] text-[#1C1B19] dark:text-zinc-100">
                                                    {item.value}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Application process */}
                    {process.length > 0 && (
                        <div className="rounded-[14px] border border-[#ECE9E3] bg-white px-[22px] py-5 dark:border-zinc-700 dark:bg-zinc-900">
                            <SectionHeading>{t('jobs.details.applicationProcess')}</SectionHeading>
                            <div className="flex flex-col gap-3.5">
                                {process.map((step, i) => (
                                    <div key={i} className="flex items-start gap-[13px]">
                                        <div className="flex h-[30px] w-[30px] flex-none items-center justify-center rounded-full bg-[#1C1B19] text-[14px] font-bold text-white dark:bg-zinc-100 dark:text-zinc-900">
                                            {toBnDigits(i + 1)}
                                        </div>
                                        <div className="pt-0.5 font-sans text-[15px] font-normal leading-[1.55] text-[#3A3733] dark:text-zinc-300">{step}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                </div>

                {/* Right sidebar */}
                <div className="flex w-full flex-none flex-col gap-[18px] lg:w-[320px]">
                    {/* Apply CTA */}
                    <div className="rounded-[14px] border border-[#ECE9E3] bg-white p-[18px] shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:border-zinc-700 dark:bg-zinc-900">
                        <div className="mb-1 flex items-center justify-between">
                            <span className="text-[14px] text-[#8A857B] dark:text-zinc-400">{t('jobs.details.applyDeadline')}</span>
                        </div>
                        <div className="mb-[3px] text-[21px] font-bold text-[#1C1B19] dark:text-zinc-100">
                            {formatBnDate(job.deadlineDate, { withYear: true })}
                        </div>
                        <div className={`mb-3.5 inline-flex items-center gap-1.5 text-[14px] font-semibold ${tone === 'red' ? 'text-[#C0563F]' : tone === 'gold' ? 'text-[#B5862B]' : 'text-[#2F7D5B]'}`}>
                            <IconClock size={14} stroke={2} />
                            {deadlineLabel(job.deadlineDate)}
                        </div>
                        {expired ? (
                            <div className="flex w-full cursor-not-allowed items-center justify-center gap-2 rounded-[11px] bg-zinc-200 p-3.5 text-[15px] font-bold text-zinc-500 dark:bg-zinc-800 dark:text-zinc-500">
                                <IconClock size={16} stroke={2} />
                                {t('jobs.details.applyClosed')}
                            </div>
                        ) : (
                            <a
                                href={job.applyUrl || '#'}
                                target={job.applyUrl ? '_blank' : undefined}
                                rel="noopener noreferrer"
                                className="flex w-full items-center justify-center gap-2 rounded-[11px] bg-primary p-3.5 text-[15px] font-bold text-white shadow-[0_4px_14px_rgba(230,138,0,0.3)] hover:bg-primary/90"
                            >
                                {t('jobs.details.applyNow')}
                                <IconArrowRight size={18} stroke={2} />
                            </a>
                        )}
                        <a
                            href={job.noticeUrl || '#'}
                            target={job.noticeUrl ? '_blank' : undefined}
                            rel="noopener noreferrer"
                            className="mt-[9px] flex w-full items-center justify-center gap-[7px] rounded-[11px] border border-[#E4E1DA] bg-white p-3 text-[15px] font-semibold text-[#56524A] hover:bg-[#FAF8F4] dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800"
                        >
                            <IconDownload size={16} stroke={1.7} />
                            {t('jobs.details.downloadNotice')}
                        </a>
                    </div>

                    {/* At a glance */}
                    <div className="rounded-[14px] border border-[#ECE9E3] bg-white px-5 py-[18px] dark:border-zinc-700 dark:bg-zinc-900">
                        <div className="mb-[13px] text-[13.5px] font-bold uppercase tracking-[0.04em] text-[#A8A294]">
                            {t('jobs.details.atAGlance')}
                        </div>
                        {glance.map(({ labelKey, value, Icon }) => (
                            <div key={labelKey} className="flex items-center gap-3 border-b border-[#F4F2EE] py-[11px] last:border-b-0 dark:border-zinc-800">
                                <div className="flex h-9 w-9 flex-none items-center justify-center rounded-[9px] bg-[#F4F2EE] dark:bg-zinc-800">
                                    <Icon size={18} stroke={1.7} className="text-[#6E6A63] dark:text-zinc-400" />
                                </div>
                                <div className="flex-1">
                                    <div className="text-[13px] text-[#8A857B] dark:text-zinc-400">
                                        {t(`jobs.details.glance.${labelKey}`)}
                                    </div>
                                    <div className="mt-0.5 text-[15.5px] font-semibold text-[#1C1B19] dark:text-zinc-100">{value}</div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Important dates */}
                    <div className="rounded-[14px] border border-[#ECE9E3] bg-white px-5 py-[18px] dark:border-zinc-700 dark:bg-zinc-900">
                        <div className="mb-[15px] text-[13.5px] font-bold uppercase tracking-[0.04em] text-[#A8A294]">
                            {t('jobs.details.importantDates')}
                        </div>
                        {timeline.map((item) => (
                            <div key={item.titleKey} className="flex items-start gap-3 pb-4 last:pb-0">
                                <span className={`mt-1 h-[11px] w-[11px] flex-none rounded-full ${item.dotClass}`} />
                                <div className="flex-1">
                                    <div className="text-[15px] font-semibold text-[#1C1B19] dark:text-zinc-100">
                                        {t(`jobs.details.timeline.${item.titleKey}`)}
                                    </div>
                                    <div className="mt-0.5 font-sans text-[13px] font-normal text-[#8A857B] dark:text-zinc-400">{item.date}</div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Biggapon poster (template + shop footer + download) */}
                    <div className="rounded-[14px] border border-[#ECE9E3] bg-white p-[18px] dark:border-zinc-700 dark:bg-zinc-900">
                        <div className="mb-[13px] text-[13.5px] font-bold uppercase tracking-[0.04em] text-[#A8A294]">
                            {t('biggapon.posterHeading')}
                        </div>
                        <div ref={posterRef} className="overflow-hidden rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.12)]">
                            <BiggaponTemplate templateId={styleId} job={job} business={posterBusiness} />
                        </div>
                        <button
                            type="button"
                            onClick={handleDownloadPoster}
                            disabled={downloadingPoster}
                            className="mt-3.5 flex w-full items-center justify-center gap-2 rounded-[11px] bg-primary p-3 text-sm font-bold text-white shadow-[0_4px_14px_rgba(230,138,0,0.3)] hover:bg-primary/90 disabled:opacity-60"
                        >
                            {downloadingPoster ? <IconLoader2 size={16} className="animate-spin" /> : <IconDownload size={16} stroke={2} />}
                            {downloadingPoster ? t('biggapon.downloading') : t('biggapon.download')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
